import React from 'react';
import { useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { getCurrentBasicEntity } from '@neptune/current-entity-business-logic';
import { getCurrentProject } from '@neptune/current-project-business-logic';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { useEntityDiagnostics } from '@neptune/entity-common-business-logic';
import { DetailsDropdownMenuContainer } from '@neptune/entity-common-feature';
import { getRunDetailsOriginRoute, useCurrentRunIdentity } from '@neptune/project-business-logic';
import { determineTab } from '@neptune/project-runs-business-logic';
import { compareDefaultTabs } from '@neptune/project-runs-domain';
import { ProjectHeader } from '@neptune/project-ui';
import {
  AutoResizingTitle,
  CopyToClipboard,
  MiddleEllipsis,
  SkeletonLoader,
} from '@neptune/shared/common-ui';
import { createNormalizedRouteTest } from '@neptune/shared/enterprise-context-util';
import { getEntityCustomId } from '@neptune/shared/entity-domain';
import { usePermissionEnhancedEntry } from '@neptune/shared/project-business-logic';
import { getCurrentRouteParams } from '@neptune/shared/routing-business-logic';
import { NavigationOptions, RouterSimpleState } from '@neptune/shared/routing-domain';
import { RouterLink } from '@neptune/shared/routing-ui';
import { Emblem, Icon, Layout } from '@neptune/shared/venus-ui';

import { ExperimentDiagnostics } from 'components/experiment-id';

type DetailsProjectHeaderContainerProps = {
  tabs: React.ReactNode;
  additionalElementRef?: React.ForwardedRef<HTMLElement>;
};

const getBackLabel = (routeName?: string) => {
  if (!routeName) {
    return 'Back';
  }

  const testRoute = createNormalizedRouteTest(routeName);

  if (testRoute('project.reports')) {
    return 'Back to report';
  }

  if (testRoute('project.runs-tab')) {
    return 'Back to runs';
  }

  if (testRoute('user-my-runs')) {
    return 'Back to my runs';
  }

  return 'Back';
};

export const DetailsProjectHeaderContainer: React.FC<DetailsProjectHeaderContainerProps> = ({
  tabs,
  additionalElementRef,
}) => {
  const project = useSelector(getCurrentProject);
  const params = useSelector(getCurrentRouteParams);
  const [runDetailsOriginRoute, setRunDetailsOriginRoute] = React.useState<
    (RouterSimpleState & NavigationOptions) | undefined
  >();
  const entity = useSelector(getCurrentBasicEntity);
  const entityWithPermissions = usePermissionEnhancedEntry(entity);
  const customRunId = entity ? getEntityCustomId(entity) : undefined;

  const compareModeFallbackTab = determineTab(compareDefaultTabs, 'table');

  useMount(() => {
    setRunDetailsOriginRoute(getRunDetailsOriginRoute());
  });

  const { currentRunIdentificationKey, currentRunName, isExperimentHead, isArchived } =
    useCurrentRunIdentity();

  const singleRunMenu = React.useMemo(() => {
    if (!entityWithPermissions) {
      return null;
    }

    return (
      <DetailsDropdownMenuContainer entityWithPermissions={entityWithPermissions} side="bottom" />
    );
  }, [entityWithPermissions]);

  const singleRunHeaderEmblem = React.useMemo(() => {
    if (isArchived) {
      return <Emblem color="plain" data-role="archived-emblem" text="Archived" size="sm" />;
    }

    if (!isExperimentHead) {
      return null;
    }

    const entityTypeFromRoute: 'experiment' | 'run' | undefined = params.type;

    if (!entityTypeFromRoute) {
      return null;
    }

    const emblemText =
      entityTypeFromRoute === 'experiment' ? 'Viewing as experiment' : 'Experiment HEAD';

    return <Emblem width="auto" text={emblemText} size="sm" color="accent" />;
  }, [params.type, isExperimentHead, isArchived]);

  const previousRouteLabel = React.useMemo(() => {
    let previousRouteName: string | undefined;

    if (runDetailsOriginRoute?.name) {
      previousRouteName = runDetailsOriginRoute.name;
    } else if (compareModeFallbackTab) {
      previousRouteName = 'project.runs-tab';
    }

    return getBackLabel(previousRouteName);
  }, [runDetailsOriginRoute, compareModeFallbackTab]);

  const BackLink = React.useCallback(() => {
    if (runDetailsOriginRoute?.name) {
      return (
        <RouterLink
          routeName={runDetailsOriginRoute.name}
          routeParams={runDetailsOriginRoute.params}
          forceContext={runDetailsOriginRoute.options?.forceContext}
        >
          <Icon
            size="lg"
            color="text"
            data-role="close-run-details"
            glyph="arrow-left"
            aria-label={previousRouteLabel}
          />
        </RouterLink>
      );
    }

    if (!compareModeFallbackTab) {
      return null;
    }

    return (
      <RouterLink
        routeName="project.runs-tab"
        routeParams={{
          ...params,
          tab: 'compare',
          dashboardId: undefined,
          runIdentificationKey: undefined,
          ...compareModeFallbackTab.routeParams,
        }}
        aria-label={previousRouteLabel}
      >
        <Icon size="lg" color="text" glyph="arrow-left" data-role="close-run-details" />
      </RouterLink>
    );
  }, [runDetailsOriginRoute, compareModeFallbackTab, params, previousRouteLabel]);

  const diagnostics = useEntityDiagnostics(entity);

  if (!project) {
    return null;
  }

  const statusEmblem = <ExperimentDiagnostics {...diagnostics} />;
  const headerContent = (
    <Layout.Row spacedChildren="sm">
      <Layout.Column span="shrink" alignItems="stretch" justifyContent="center">
        <BackLink />
      </Layout.Column>
      <Layout.Column>
        <Layout.Row>
          {currentRunName !== undefined ? (
            <AutoResizingTitle
              data-role="selected-run-name"
              data-id={currentRunIdentificationKey}
              title={currentRunName}
              hoverAddon={<CopyToClipboard text={currentRunName} />}
            />
          ) : (
            <SkeletonLoader />
          )}
        </Layout.Row>
        {customRunId ? (
          <Layout.Row span="grow" spacedChildren="xs" alignItems="center">
            <MiddleEllipsis text={customRunId} size="sm" color="text-secondary" />
            <CopyToClipboard text={customRunId} />
          </Layout.Row>
        ) : null}
      </Layout.Column>
    </Layout.Row>
  );

  return (
    <ProjectHeader
      tabs={tabs}
      headerContent={headerContent}
      singleRunEmblem={singleRunHeaderEmblem}
      singleRunMenu={singleRunMenu}
      singleRunStatus={statusEmblem}
      additionalElementRef={additionalElementRef}
    />
  );
};
